// Sibling of connect-requirements.js, and the same contract: every code the
// backend can emit gets readable English, and one it cannot emit yet is
// humanised rather than dropped.
//
// `owner` is the part that matters on screen. An admin who cannot tell which
// blockers are theirs prepares five terminals, stays red, and goes looking for
// a bug — which is exactly the live state of the QA company today.
const BLOCKERS = Object.assign(Object.create(null), {
  missing_account: {
    label: 'Stripe account not created',
    hint: 'Start onboarding from the panel above',
    owner: 'restaurant',
  },
  not_ready: {
    label: 'Stripe has not finished verifying the restaurant',
    hint: 'The restaurant finishes this on its own Settings page',
    owner: 'restaurant',
  },
  fee_payer: {
    label: 'Stripe bills this account’s fees to Thisway',
    hint: 'Re-create the Stripe account below; the restaurant then onboards again',
    owner: 'admin',
  },
  charges: {
    label: 'Stripe has not enabled charges',
    hint: 'Usually clears once verification completes',
    owner: 'restaurant',
  },
  payouts: {
    label: 'Stripe has not enabled payouts',
    hint: 'Usually a missing bank account',
    owner: 'restaurant',
  },
  terminal: {
    label: 'Some terminals have no Stripe location',
    hint: 'Prepare them in the section below',
    owner: 'admin',
  },
  webhook_secret: {
    label: 'Connect webhook secret is not configured',
    hint: 'A deployment setting — nobody can fix this from the dashboard',
    owner: 'environment',
  },
  webhook_event: {
    label: 'No account update has ever arrived from Stripe',
    hint: 'The Connect webhook endpoint has to be registered in Stripe first',
    owner: 'environment',
  },
  already_active: {
    label: 'This company is already on direct charges',
    hint: 'Nothing to do',
    owner: 'admin',
  },
  already_legacy: {
    label: 'This company is already on the legacy platform account',
    hint: 'Nothing to do',
    owner: 'admin',
  },
});

const humanize = (code) => {
  const words = String(code).replace(/[._]+/g, ' ').trim();
  if (!words) return 'Unknown check';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const describeBlocker = (code) => {
  const entry = BLOCKERS[code];
  if (entry) return entry;
  // Unmapped: readable, and owned by nobody we can name — which is the honest
  // answer, and keeps it on screen instead of vanishing.
  return { label: humanize(code), hint: '', owner: 'environment' };
};

const GROUP_ORDER = [
  { key: 'restaurant', title: 'Waiting on the restaurant' },
  { key: 'admin', title: 'Yours to fix here' },
  { key: 'environment', title: 'Needs an environment change' },
];

export const groupBlockers = (codes) => {
  const list = Array.isArray(codes) ? codes : [];
  return GROUP_ORDER.map(({ key, title }) => ({
    key,
    title,
    items: list
      .filter((code) => typeof code === 'string' && describeBlocker(code).owner === key)
      .map((code) => ({ code, ...describeBlocker(code) })),
  })).filter((group) => group.items.length > 0);
};
